/**
 * 1.6.7 (D-1.6.7-4) — the Tech Support gate on Book → Tech Support, shown in place of the day and time chips while
 * the person is not onboarded for Tech Support: the reason, naming the piece, and two presses —
 *   Complete it for them   the agreement to read to the person, their name typed, "I agree", Record;
 *   Send by email          the email as it will go, to review; "Queue this email" puts it in the Outbox.
 * The same routes the agreements card uses (POST <person>/agreements/tech-agreement/complete, GET and POST
 * …/email) and the same signing piece (AgreementSign); nothing signs differently here. Both open in place, inside
 * the sheet. When the agreement is recorded the sheet reads the onboarding again and offers the days.
 *
 * Those routes are the office's (administrators and co-administrators). A scheduler sees the reason and where it
 * is completed, without the two buttons.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { AgreementEmailPreview, AgreementLinesResponse, AgreementSendResponse, OnBehalfInput, OnBehalfResponse } from '@shared/agreement-cards';
import type { Onboarding } from '@shared/onboarding';
import { staffName } from '@shared/people';
import { apiFetch } from '@/lib/api';
import { useMe } from '@/auth/useMe';
import { useAnnounce } from '@/components/Announcer';
import { EmailPreview } from '@/components/EmailPreview';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { errorText, personPath } from '@/features/people/api';
import { AgreementSign } from './AgreementSign';
import { pieceList } from './onboarding';

const KIND = 'tech-agreement' as const;

export interface OnboardingGateProps {
  tenant: { tenantId: string; unit: string; name: string };
  /** Their onboarding for Tech Support (not complete). */
  onboarding: Onboarding;
  /** The agreement was recorded: read the onboarding again (the days are offered once it is complete). */
  onRecorded: () => void;
  /** Leaving the sheet for the person's record. */
  onLeave?: () => void;
}

export function OnboardingGate({ tenant, onboarding, onRecorded, onLeave }: OnboardingGateProps) {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const announce = useAnnounce();
  const toast = useToast();
  const [mode, setMode] = useState<'complete' | 'send' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queuedTo, setQueuedTo] = useState<string | null>(null);
  /** The button that opened a panel: when the panel closes, focus goes back to it (it is drawn again then). */
  const opened = useRef<'complete' | 'send' | null>(null);
  const completeBtn = useRef<HTMLButtonElement>(null);
  const sendBtn = useRef<HTMLButtonElement>(null);
  const panelHead = useRef<HTMLHeadingElement>(null);
  const roles = me.data?.principal?.userRoles ?? [];
  const office = roles.includes('admin') || roles.includes('coadmin');
  const base = personPath(tenant.tenantId);
  const needsAgreement = onboarding.missing.includes(KIND);
  const program = t(`onb167.program.${onboarding.program}`);
  const reason = t('onb167.reason', { name: tenant.name, program, done: onboarding.done, total: onboarding.total, list: pieceList(t, i18n.language, onboarding.missing) });

  // the card's own read: the person's language, whether an email can go, whether one was already sent
  const lines = useQuery({ queryKey: ['agreement-lines', base], queryFn: () => apiFetch<AgreementLinesResponse>(`${base}/agreements`), enabled: office && needsAgreement, staleTime: 15_000, retry: false });
  const line = lines.data?.lines.find((l) => l.kind === KIND) ?? null;
  const preview = useQuery({ queryKey: ['agreement-email', base, KIND], queryFn: () => apiFetch<AgreementEmailPreview>(`${base}/agreements/${KIND}/email`), enabled: mode === 'send', staleTime: 0, retry: false });
  const noSend = line?.blocked ?? (line?.noSend ? t(`onb167.gate.noSend.${line.noSend}`) : null);
  const day = (iso: string) => new Intl.DateTimeFormat(i18n.language.startsWith('es') ? 'es-US' : 'en-US', { dateStyle: 'medium', timeZone: 'America/New_York' }).format(new Date(iso));
  const staff = staffName(me.data?.principal?.userDetails ?? '');
  const name = lines.data?.name || tenant.name;

  useEffect(() => {
    announce(reason, 'assertive');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.tenantId]);
  useEffect(() => {
    if (mode) panelHead.current?.focus();
    else if (opened.current) {
      (opened.current === 'complete' ? completeBtn : sendBtn).current?.focus();
      opened.current = null;
    }
  }, [mode]);

  const show = (m: 'complete' | 'send') => {
    opened.current = m;
    setError(null);
    setMode(m);
  };
  const back = () => {
    setMode(null);
    setError(null);
  };
  const press = async <T,>(path: string, body: unknown, then: (r: T) => void) => {
    setBusy(true);
    setError(null);
    try {
      then(await apiFetch<T>(`${base}/agreements/${KIND}/${path}`, { method: 'POST', body: JSON.stringify(body ?? {}) }));
    } catch (e) {
      const m = errorText(e);
      setError(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };
  const complete = (input: OnBehalfInput) =>
    press<OnBehalfResponse>('complete', input, () => {
      const m = t('onb167.gate.recorded');
      toast(m);
      announce(m);
      opened.current = null; // the sheet takes the focus to its question (the days are next)
      setMode(null);
      onRecorded();
    });
  const queue = () =>
    press<AgreementSendResponse>('email', {}, (r) => {
      const to = r.email ?? preview.data?.to ?? '';
      const m = t('onb167.gate.queued', { to });
      setQueuedTo(to);
      toast(m);
      announce(m);
      void lines.refetch();
      back();
    });

  if (mode === 'complete') {
    return (
      <section className="onb-panel" aria-labelledby="onb-panel-h" data-testid="onb-gate-complete">
        <h4 id="onb-panel-h" ref={panelHead} tabIndex={-1}>
          {t('onb167.gate.completeTitle', { name })}
        </h4>
        <p className="help">{t('onb167.gate.completeHelp', { name })}</p>
        <AgreementSign kind={KIND} lang={lines.data?.lang ?? 'en'} desk={{ name, staff }} busy={busy} error={error} onSign={(input) => void complete(input)} />
        <Button variant="secondary" type="button" onClick={back} data-testid="onb-gate-back">
          {t('onb167.gate.back')}
        </Button>
      </section>
    );
  }
  if (mode === 'send') {
    const p = preview.data;
    const problem = error ?? (preview.error ? errorText(preview.error) : null);
    return (
      <section className="onb-panel" aria-labelledby="onb-panel-h" data-testid="onb-gate-send-panel">
        <h4 id="onb-panel-h" ref={panelHead} tabIndex={-1}>
          {t('onb167.gate.sendTitle', { name })}
        </h4>
        <p className="help">{t('onb167.gate.sendHelp')}</p>
        {p ? (
          <>
            <p>
              <span className="lbl">{t('onb167.gate.sendTo')}</span> <span data-testid="onb-gate-send-to">{p.to}</span>
            </p>
            <EmailPreview code={p.code} subject={p.subject} body={p.body} lang={p.lang} buttons={[p.button]} office={p.office} tokens={{}} testId="onb-gate-email-preview" />
            {p.placeholder ? (
              <p className="help" role="note">
                {t('onb167.gate.placeholder')}
              </p>
            ) : null}
            <p className="help">{p.windowMinutes === null ? t('onb167.gate.windowMorning') : t('onb167.gate.window', { count: p.windowMinutes })}</p>
          </>
        ) : preview.error ? null : (
          <p className="help" role="status">
            {t('onb167.gate.loading')}
          </p>
        )}
        {problem ? (
          <p className="field-err" role="alert" data-testid="onb-gate-error">
            <Icon name="alert" width={16} height={16} /> {problem}
          </p>
        ) : null}
        <div className="onb-actions">
          <Button variant="secondary" type="button" onClick={back} data-testid="onb-gate-back">
            {t('onb167.gate.back')}
          </Button>
          {p ? (
            <Button type="button" disabled={busy} onClick={() => queue()} data-testid="onb-gate-queue">
              <Icon name="mail" /> {t('onb167.gate.queue')}
            </Button>
          ) : null}
        </div>
      </section>
    );
  }

  const sentNote = queuedTo ? t('onb167.gate.queued', { to: queuedTo }) : line?.sent ? t('onb167.gate.sentNote', { date: day(line.sent.at) }) : null;
  return (
    <div className="warn take onb-gate" role="group" aria-label={t('onb167.gate.label', { program })} data-testid="onb-gate" data-program={onboarding.program}>
      <Icon name="alert" />
      <div className="onb-gate-b">
        <b data-testid="onb-gate-reason">{reason}</b>
        {needsAgreement ? <span>{t('onb167.gate.waits')}</span> : null}
        {sentNote ? (
          <span role="status" data-testid="onb-gate-sent">
            {sentNote}
          </span>
        ) : null}
        {needsAgreement && office ? (
          <>
            {noSend ? (
              <span className="onb-why" id="onb-gate-nosend" data-testid="onb-gate-nosend">
                {noSend}
              </span>
            ) : null}
            <div className="onb-actions">
              <Button ref={completeBtn} type="button" disabled={!!line?.blocked} onClick={() => show('complete')} data-testid="onb-gate-complete-open">
                <Icon name="pencil" /> {t('onb167.gate.complete')}
              </Button>
              <Button
                variant="secondary"
                type="button"
                disabled={!!noSend || lines.isLoading}
                aria-describedby={noSend ? 'onb-gate-nosend' : undefined}
                ref={sendBtn}
                onClick={() => show('send')}
                data-testid="onb-gate-send"
              >
                <Icon name="mail" /> {line?.sent || queuedTo ? t('onb167.gate.resend') : t('onb167.gate.send')}
              </Button>
            </div>
          </>
        ) : (
          <span data-testid="onb-gate-office">{t(needsAgreement ? 'onb167.gate.office' : 'onb167.gate.profile')}</span>
        )}
        {office && onboarding.missing.some((p) => p !== KIND) ? (
          <Link className="btn secondary onb-link" to={`/people/${encodeURIComponent(tenant.tenantId)}`} onClick={onLeave} data-testid="onb-gate-record">
            {t('onb167.gate.openRecord', { unit: tenant.unit })}
          </Link>
        ) : null}
      </div>
    </div>
  );
}
